import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, FileText, Shield, Download, Eye, Clock, Building2, Send, Upload } from "lucide-react";
import EntityLink from "../components/shared/EntityLink";
import EntityNotes from "../components/shared/EntityNotes";
import { Entity360Shell, TimelineCard } from "../components/shared/OSComponents";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIS_COLORS = { public: "bg-emerald-100 text-emerald-600", deal_room: "bg-blue-100 text-blue-600", internal: "bg-amber-100 text-amber-600" };

export default function Document360Page({ docId, onBack, breadcrumbs = [] }) {
  const { navigateToEntity } = usePageNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/entity/document/${docId}/360`, { headers }).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [docId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]">Document not found</div>;

  const { document: doc, linked_entity, uploader, ai_analysis, access_history } = data;
  const crumbs = [...breadcrumbs, { type: "document", id: docId, label: doc.original_filename || "Document" }];

  const relatedEntities = [
    ...(linked_entity ? [{ type: linked_entity.type, id: linked_entity.id, title: linked_entity.label || linked_entity.full_name, subtitle: linked_entity.type }] : []),
    ...(uploader ? [{ type: uploader.role === "lender" ? "lender" : uploader.role === "investor" ? "investor" : "borrower", id: uploader.user_id, title: uploader.full_name, subtitle: uploader.role }] : []),
  ];

  const quickActions = [
    ...(doc.url ? [{ id: "download", label: "Download Document", icon: Download, onClick: () => window.open(doc.url, "_blank") }] : []),
    ...(linked_entity ? [{ id: "view_entity", label: `View ${linked_entity.type}`, icon: Building2, onClick: () => onNavigate(linked_entity.type, linked_entity.id) }] : []),
    { id: "add_note", label: "Add Note", icon: Send, onClick: () => setTab("notes") },
  ];

  const tabs = [
    { key: "details", label: "Details" },
    ...(ai_analysis ? [{ key: "analysis", label: "AI Analysis" }] : []),
    ...(access_history?.length ? [{ key: "audit", label: `Audit (${access_history.length})` }] : []),
    { key: "notes", label: "Notes" },
  ];

  const kpis = [
    { label: "Type", value: doc.doc_type || "—", icon: FileText },
    { label: "Visibility", value: doc.visibility || "—", icon: Eye },
    ...(doc.file_size ? [{ label: "Size", value: `${(doc.file_size / 1024).toFixed(0)} KB`, icon: FileText }] : []),
  ];

  const badges = [{ label: doc.visibility || "unknown", className: VIS_COLORS[doc.visibility] || "bg-gray-100 text-gray-600" }];

  return (
    <Entity360Shell type="document" title={doc.original_filename}
      subtitle={[doc.doc_type, doc.created_at && new Date(doc.created_at).toLocaleDateString()].filter(Boolean).join(" | ")}
      badges={badges} kpis={kpis} tabs={tabs} activeTab={tab} onTabChange={setTab}
      onBack={onBack} breadcrumbs={crumbs} onNavigate={navigateToEntity}
      quickActions={quickActions} relatedEntities={relatedEntities}>

      {tab === "details" && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Metadata</h3>
            <div className="space-y-2 text-xs">
              {[["Type", doc.doc_type], ["Visibility", doc.visibility], ["Size", doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : null], ["Uploaded", doc.created_at ? new Date(doc.created_at).toLocaleString() : null]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between"><span className="text-[#888]">{k}</span><span className="font-semibold">{v}</span></div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Linked Entity</h3>
            {linked_entity ? (
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-emerald-600" />
                <div><EntityLink type={linked_entity.type} id={linked_entity.id} label={linked_entity.label} onClick={navigateToEntity} />{linked_entity.full_name && <p className="text-[10px] text-[#999]">{linked_entity.full_name}</p>}</div>
              </div>
            ) : <p className="text-xs text-[#888]">No linked entity</p>}
          </div>
          {uploader && (
            <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">Uploader</h3>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center text-sm font-bold text-blue-700">{uploader.full_name?.charAt(0)}</div>
                <div><EntityLink type={uploader.role === "lender" ? "lender" : uploader.role === "investor" ? "investor" : "borrower"} id={uploader.user_id} label={uploader.full_name} onClick={navigateToEntity} />{uploader.email && <p className="text-[10px] text-[#999]">{uploader.email}</p>}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "analysis" && ai_analysis && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-3">AI Analysis</h3>
          <div className="text-xs text-[#555] whitespace-pre-wrap max-h-64 overflow-y-auto">{typeof ai_analysis === "string" ? ai_analysis : JSON.stringify(ai_analysis, null, 2)}</div>
        </div>
      )}

      {tab === "audit" && access_history && (
        <TimelineCard events={access_history.map(a => ({ event: a.action, timestamp: a.created_at, description: `${a.action} by ${a.user_id?.slice(0, 8)}` }))} />
      )}

      {tab === "notes" && <EntityNotes entityType="document" entityId={docId} />}
    </Entity360Shell>
  );
}
